import React, { PropsWithChildren } from "react";

import Text from "@src/sharedComponents/Typography/Text";

import MenuOptionWithCheckIfSelected from "./MenuOptionWithCheckIfSelected";

import styles from "./MenuOptionsWithCheckIfSelected.module.css";

export interface DropdownMenuOption<ID> {
  id: ID;
  label: string;
  label2?: string;
}

export interface CategoryWithOptions<
  Category extends string,
  DropdownMenuOption
> {
  category: Category;
  options: DropdownMenuOption[];
}

interface Props<
  ID extends string,
  T extends DropdownMenuOption<ID>,
  Category extends string,
  C extends CategoryWithOptions<Category, T>
> {
  curItemId?: ID;
  curItemName?: string;
  options?: T[];
  categoryWithOptions?: C[];
  displayLabel2InCategoryWithOptions?: boolean;
  onSelect: (el: T, cat?: Category) => void;
  maxHeight?: number;
  labelClassName?: string;
  label2ClassName?: string;
  width?: React.CSSProperties["width"];
  hideCheckmark?: boolean;
  isLoading?: boolean;
  shouldRenderTooltip?: (optionLabel: string) => boolean;
}

const MenuOptionsWithCheckIfSelected = <
  ID extends string,
  T extends DropdownMenuOption<ID>,
  Category extends string,
  C extends CategoryWithOptions<Category, T>
>({
  curItemId,
  curItemName,
  options,
  categoryWithOptions,
  displayLabel2InCategoryWithOptions,
  onSelect,
  maxHeight,
  labelClassName,
  label2ClassName,
  children, // this is used for the addition of a search bar
  width,
  hideCheckmark,
  isLoading,
  shouldRenderTooltip,
}: PropsWithChildren<Props<ID, T, Category, C>>) => {
  if (options === undefined && categoryWithOptions === undefined) {
    throw new Error(
      "DropdownMenuOptions: options and categoryWithOptions are both undefined"
    );
  }

  if ((options?.length ?? 0) > 0 && (categoryWithOptions?.length ?? 0) > 0) {
    throw new Error(
      "DropdownMenuOptions: options and categoryWithOptions cannot both be used together"
    );
  }

  const handleOnSelect = (el: T, cat?: Category) => () => onSelect(el, cat);

  return (
    <div className={styles.container} style={{ width }}>
      {children}

      <div
        className={styles.scrollContainer}
        style={maxHeight ? { maxHeight: `${maxHeight}px` } : {}}
      >
        {(options ?? []).length === 0 &&
          (categoryWithOptions ?? []).length === 0 && (
            <>
              {isLoading === true ? (
                <MenuOptionWithCheckIfSelected
                  key={"none"}
                  label={"Loading..."}
                  isChecked={false}
                  onSelect={() => undefined}
                  isDisabled={true}
                  shouldRenderTooltip={shouldRenderTooltip}
                />
              ) : (
                <MenuOptionWithCheckIfSelected
                  key={"none"}
                  label={"No options..."}
                  isChecked={false}
                  onSelect={() => undefined}
                  isDisabled={true}
                  shouldRenderTooltip={shouldRenderTooltip}
                />
              )}
            </>
          )}

        {(options ?? []).map((el) => {
          const isMatch =
            curItemId !== undefined
              ? curItemId === el.id
              : curItemName !== undefined && el.label === curItemName;

          const isChecked = hideCheckmark ? false : isMatch;

          return (
            <MenuOptionWithCheckIfSelected
              key={el.id}
              label={el.label}
              label2={el.label2}
              isChecked={isChecked}
              onSelect={handleOnSelect(el)}
              labelClassName={labelClassName}
              label2ClassName={label2ClassName}
              shouldRenderTooltip={shouldRenderTooltip}
            />
          );
        })}

        {(categoryWithOptions ?? []).map((cat) => {
          return (
            <div key={cat.category} className={styles.category}>
              <Text
                variant="body"
                color={"textSubtle"}
                className={styles.categoryLabel}
              >
                {cat.category}
              </Text>

              {cat.options.map((el) => {
                return (
                  <MenuOptionWithCheckIfSelected
                    key={el.id}
                    label={el.label}
                    label2={
                      displayLabel2InCategoryWithOptions ? el.label2 : undefined
                    }
                    isChecked={
                      hideCheckmark
                        ? false
                        : curItemId !== undefined
                        ? curItemId === el.id
                        : curItemName !== undefined && el.label === curItemName
                    }
                    onSelect={handleOnSelect(el, cat.category)}
                    shouldRenderTooltip={shouldRenderTooltip}
                  />
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MenuOptionsWithCheckIfSelected;
